import React, { useEffect, useMemo, useRef, useState } from 'react';
import './_TreeSelectInput.css';

// A single-select hierarchical picker. Replaces the old MVC branch /
// office-department tree partials (_BranchDepartmentSelectView.cshtml /
// _OfficeDepartmentSelectView.cshtml): a read-only text input that opens a
// collapsible tree; clicking a node fills the input and stores its id.

export type TreeNode = {
    id?: string;
    name?: string;
    parentId?: string;
};

type Props = {
    title: string;
    placeholder?: string;
    items: TreeNode[];
    value?: string; // selected id
    valueName?: string; // selected display name (used until the user picks)
    onChange: (id: string | undefined, name: string | undefined) => void;
    errorText?: string;
};

type BuiltNode = TreeNode & { children: BuiltNode[] };

// Builds a parent/child forest from the flat id/parentId list. Any item whose
// parentId is missing (or points outside the set) is treated as a root.
const buildForest = (items: TreeNode[]): BuiltNode[] => {
    const byId = new Map<string, BuiltNode>();
    items.forEach((it) => {
        if (it.id) byId.set(it.id, { ...it, children: [] });
    });
    const roots: BuiltNode[] = [];
    byId.forEach((node) => {
        const parent = node.parentId ? byId.get(node.parentId) : undefined;
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    });
    return roots;
};

const TreeSelectInput = (props: Props) => {
    const { title, placeholder, items, value, valueName, onChange, errorText } = props;

    const [open, setOpen] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const wrapRef = useRef<HTMLDivElement>(null);

    const forest = useMemo(() => buildForest(items || []), [items]);

    // The display name: prefer the explicitly selected name, otherwise resolve
    // the current value id against the items.
    const display = useMemo(() => {
        if (valueName) return valueName;
        if (value) {
            const found = (items || []).find((i) => i.id === value);
            return found ? found.name || '' : '';
        }
        return '';
    }, [value, valueName, items]);

    // Close the tree when clicking anywhere outside the widget.
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const toggleExpand = (id: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const select = (node: BuiltNode) => {
        onChange(node.id, node.name);
        setOpen(false);
    };

    const clear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(undefined, undefined);
    };

    const renderNodes = (nodes: BuiltNode[]) => (
        <ul className="tsi-nested">
            {nodes.map((node) => {
                const hasChildren = node.children.length > 0;
                const isOpen = node.id ? expanded.has(node.id) : false;
                return (
                    <li className="tsi-li" key={node.id}>
                        <span className="tsi-caret">
                            {hasChildren && (
                                <span
                                    className={`tsi-arrow ${isOpen ? 'tsi-arrow-down' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (node.id) toggleExpand(node.id);
                                    }}
                                />
                            )}
                            <span
                                className={`tsi-item ${node.id === value ? 'tsi-item-selected' : ''} ${
                                    hasChildren ? '' : 'tsi-leaf'
                                }`}
                                onClick={() => select(node)}
                            >
                                {node.name}
                            </span>
                        </span>
                        {hasChildren && isOpen && renderNodes(node.children)}
                    </li>
                );
            })}
        </ul>
    );

    return (
        <div className="tsi-wrap" ref={wrapRef}>
            {title && (
                <div className="minor-title-box text-right">
                    <span className="title">{title}</span>
                </div>
            )}
            <div className="tsi-input-row">
                <input
                    className="form-control mySelect"
                    type="text"
                    readOnly
                    autoComplete="off"
                    placeholder={placeholder || title}
                    value={display}
                    onClick={() => setOpen((o) => !o)}
                />
                {display && <span className="tsi-clear" title="נקה" onClick={clear} />}
            </div>
            {open && (
                <div className="tsi-tree">
                    {forest.length > 0 ? (
                        renderNodes(forest)
                    ) : (
                        <div className="tsi-empty">אין נתונים</div>
                    )}
                </div>
            )}
            {errorText && <span className="text-danger">{errorText}</span>}
        </div>
    );
};

export default TreeSelectInput;
